// YOSHI-10464: the 18 operations of the public Yoshi API, as the portal documents them.
//
// The contract this mirrors lives in yoshi-api at docs/openapi/yoshi-api.v1.yaml and is
// the source of truth: paths, parameters and scopes are copied from it, and the response bodies come
// from ./examples.ts, which is generated from that same file. A change to the contract is made there
// first and brought here afterwards.
//
// Each operation carries one sample call in `example`, and ./codeSamples.ts generates the cURL,
// JavaScript, Python, PHP and C# snippets from it. There is no snippet written by hand, so a change
// to a path or to a parameter cannot leave half the examples lying.
//
// Everything technical stays in this file untranslated - parameter names, types and JSON bodies read
// the same in every language. Only the prose is translated, and it travels as the `translationKey`
// of each parameter and of each operation.

import { DocsEndpoint, DocsError, DocsParam } from "../interfaces";
import {
  CATEGORY_TOTALS_EXAMPLE,
  CONCESSION_EXAMPLE,
  CONCESSION_TOTALS_EXAMPLE,
  ENUMS_EXAMPLE,
  EVENT_CONCESSIONS_LIST_EXAMPLE,
  EVENT_EXAMPLE,
  EVENT_TOTALS_EXAMPLE,
  EVENTS_LIST_EXAMPLE,
  PAYMENT_TYPE_TOTALS_EXAMPLE,
  PRODUCT_TOTALS_EXAMPLE,
  SALE_EXAMPLE,
  SALES_LIST_EXAMPLE,
  TOKEN_RESPONSE_EXAMPLE,
  VENUE_CONCESSIONS_LIST_EXAMPLE,
  VENUE_EXAMPLE,
  VENUES_LIST_EXAMPLE,
} from "./examples";

/**
 * YOSHI-10464: the single address every route of the contract hangs off, and the one every example
 * of the portal runs against. An integrator knows one address, so the portal names one: a test
 * endpoint, when it is needed, is handed over privately together with the credential issued for it.
 */
export const API_BASE_URL: string = "https://api.yoshicash.com/partners";

/** The OpenAPI contract, served from public/ so an integrator can import it in its own tooling. */
export const SPEC_FILE_URL: string = "/yoshi-api.v1.yaml";
export const SPEC_FILE_NAME: string = "yoshi-api.v1.yaml";

/** The Postman collection of the same contract, ready to import and run. */
export const POSTMAN_FILE_URL: string =
  "/yoshi-api.postman_collection.json";
export const POSTMAN_FILE_NAME: string =
  "yoshi-api.postman_collection.json";

/** Hard limits of the contract, stated once so the pages cannot disagree with each other. */
export const MAX_DATE_RANGE_DAYS: number = 92;
export const DEFAULT_PAGE_LIMIT: number = 50;
export const MAX_PAGE_LIMIT: number = 100;

/**
 * Access token lifetime. The default is what a credential gets when no other one is agreed, and the
 * bounds are what the API accepts; the effective value always travels in `expires_in`, which is why
 * every page tells the integrator to read it from the response instead of hardcoding a number.
 */
export const DEFAULT_TOKEN_TTL_SECONDS: number = 86400;
export const MIN_TOKEN_TTL_SECONDS: number = 300;
export const MAX_TOKEN_TTL_SECONDS: number = 604800;

/** Sample uuids of the contract examples, reused so every snippet tells the same story. */
const EVENT_UUID: string = "3a7f5e21-8c94-4b0d-9e15-6f2a8b3c4d5e";
const VENUE_UUID: string = "0f1c2d3e-4a5b-4c6d-8e9f-a0b1c2d3e4f5";
const CONCESSION_UUID: string = "5c8a1b2d-9e3f-4a07-b6c5-1d2e3f4a5b6c";
const SALE_UUID: string = "b21d7f3a-4c58-4e90-a1b6-8d0e2f4a6c37";

/**
 * Placeholders of the values a snippet must never carry pasted. Each generator of ./codeSamples.ts
 * swaps them for a variable read from the environment in its own language.
 */
export const ACCESS_TOKEN_PLACEHOLDER: string = "__ACCESS_TOKEN__";
export const CLIENT_ID_PLACEHOLDER: string = "__CLIENT_ID__";
export const CLIENT_SECRET_PLACEHOLDER: string = "__CLIENT_SECRET__";

/** Header values of the sample calls, resolved by name when a snippet is generated. */
export const HEADER_EXAMPLE_VALUES: Record<string, string> = {
  Authorization: `Bearer ${ACCESS_TOKEN_PLACEHOLDER}`,
  "Content-Type": "application/json",
  "Accept-Language": "es",
};

/** The header every route but the token route requires. */
const AUTHORIZATION_HEADER: DocsParam = {
  name: "Authorization",
  location: "header",
  type: "string",
  isRequired: true,
  translationKey: "authorizationHeader",
};

const CONTENT_TYPE_HEADER: DocsParam = {
  name: "Content-Type",
  location: "header",
  type: "string",
  isRequired: true,
  translationKey: "contentTypeHeader",
};

const ACCEPT_LANGUAGE_HEADER: DocsParam = {
  name: "Accept-Language",
  location: "header",
  type: "string",
  isRequired: false,
  translationKey: "acceptLanguageHeader",
};

export const DOCS_ENDPOINTS: DocsEndpoint[] = [
  {
    id: "issuePartnerToken",
    method: "POST",
    path: "/v1/oauth/token",
    group: "authentication",
    scope: null,
    params: [
      CONTENT_TYPE_HEADER,
      { name: "client_id", location: "body", type: "string", isRequired: true, translationKey: "clientId" },
      { name: "client_secret", location: "body", type: "string", isRequired: true, translationKey: "clientSecret" },
    ],
    example: {
      path: "/v1/oauth/token",
      query: {},
      body: {
        client_id: CLIENT_ID_PLACEHOLDER,
        client_secret: CLIENT_SECRET_PLACEHOLDER,
      },
    },
    response: TOKEN_RESPONSE_EXAMPLE,
  },
  {
    id: "listPartnerVenues",
    method: "GET",
    path: "/v1/venues",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "startingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "endingBefore" },
    ],
    example: { path: "/v1/venues", query: { limit: "50" }, body: null },
    response: VENUES_LIST_EXAMPLE,
  },
  {
    id: "getPartnerVenue",
    method: "GET",
    path: "/v1/venues/{uuid}",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "venuePath" },
    ],
    example: { path: `/v1/venues/${VENUE_UUID}`, query: {}, body: null },
    response: VENUE_EXAMPLE,
  },
  {
    id: "listPartnerVenueConcessions",
    method: "GET",
    path: "/v1/venues/{uuid}/concessions",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "venuePath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "startingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "endingBefore" },
    ],
    example: {
      path: `/v1/venues/${VENUE_UUID}/concessions`,
      query: {},
      body: null,
    },
    response: VENUE_CONCESSIONS_LIST_EXAMPLE,
  },
  {
    id: "getPartnerConcession",
    method: "GET",
    path: "/v1/concessions/{uuid}",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "concessionPath" },
    ],
    example: {
      path: `/v1/concessions/${CONCESSION_UUID}`,
      query: {},
      body: null,
    },
    response: CONCESSION_EXAMPLE,
  },
  {
    id: "listPartnerEvents",
    method: "GET",
    path: "/v1/events",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "venue", location: "query", type: "string (uuid)", isRequired: false, translationKey: "eventVenueFilter" },
      { name: "status", location: "query", type: "string", isRequired: false, translationKey: "eventStatusFilter" },
      { name: "from", location: "query", type: "string (date-time)", isRequired: false, translationKey: "fromEvents" },
      { name: "to", location: "query", type: "string (date-time)", isRequired: false, translationKey: "to" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "startingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "endingBefore" },
    ],
    example: {
      path: "/v1/events",
      query: {
        venue: VENUE_UUID,
        from: "2026-09-01T00:00:00Z",
        to: "2026-09-30T23:59:59Z",
      },
      body: null,
    },
    response: EVENTS_LIST_EXAMPLE,
  },
  {
    id: "getPartnerEvent",
    method: "GET",
    path: "/v1/events/{uuid}",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
    ],
    example: { path: `/v1/events/${EVENT_UUID}`, query: {}, body: null },
    response: EVENT_EXAMPLE,
  },
  {
    id: "listPartnerEventConcessions",
    method: "GET",
    path: "/v1/events/{uuid}/concessions",
    group: "structure",
    scope: "events:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "startingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "endingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/concessions`,
      query: {},
      body: null,
    },
    response: EVENT_CONCESSIONS_LIST_EXAMPLE,
  },
  {
    id: "getPartnerEnums",
    method: "GET",
    path: "/v1/enums",
    group: "catalogs",
    scope: null,
    params: [AUTHORIZATION_HEADER, ACCEPT_LANGUAGE_HEADER],
    example: { path: "/v1/enums", query: {}, body: null },
    response: ENUMS_EXAMPLE,
  },
  {
    id: "listPartnerSales",
    method: "GET",
    path: "/v1/sales",
    group: "sales",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "event", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleEventFilter" },
      { name: "venue", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleVenueFilter" },
      { name: "concession", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleConcessionFilter" },
      { name: "from", location: "query", type: "string (date-time)", isRequired: false, translationKey: "fromSales" },
      { name: "to", location: "query", type: "string (date-time)", isRequired: false, translationKey: "to" },
      { name: "status", location: "query", type: "string", isRequired: false, translationKey: "saleStatusFilter" },
      { name: "payment_type", location: "query", type: "string", isRequired: false, translationKey: "salePaymentTypeFilter" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleStartingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleEndingBefore" },
    ],
    example: {
      path: "/v1/sales",
      query: { event: EVENT_UUID, limit: "100" },
      body: null,
    },
    response: SALES_LIST_EXAMPLE,
  },
  {
    id: "getPartnerSale",
    method: "GET",
    path: "/v1/sales/{uuid}",
    group: "sales",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "salePath" },
    ],
    example: { path: `/v1/sales/${SALE_UUID}`, query: {}, body: null },
    response: SALE_EXAMPLE,
  },
  {
    id: "listPartnerEventSales",
    method: "GET",
    path: "/v1/events/{uuid}/sales",
    group: "sales",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "venue", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleVenueFilter" },
      { name: "concession", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleConcessionFilter" },
      { name: "from", location: "query", type: "string (date-time)", isRequired: false, translationKey: "fromSales" },
      { name: "to", location: "query", type: "string (date-time)", isRequired: false, translationKey: "to" },
      { name: "status", location: "query", type: "string", isRequired: false, translationKey: "saleStatusFilter" },
      { name: "payment_type", location: "query", type: "string", isRequired: false, translationKey: "salePaymentTypeFilter" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleStartingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleEndingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/sales`,
      query: { concession: CONCESSION_UUID, limit: "100" },
      body: null,
    },
    response: SALES_LIST_EXAMPLE,
  },
  {
    id: "listPartnerConcessionSales",
    method: "GET",
    path: "/v1/concessions/{uuid}/sales",
    group: "sales",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "concessionPath" },
      { name: "event", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleEventFilter" },
      { name: "venue", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleVenueFilter" },
      { name: "from", location: "query", type: "string (date-time)", isRequired: false, translationKey: "fromSales" },
      { name: "to", location: "query", type: "string (date-time)", isRequired: false, translationKey: "to" },
      { name: "status", location: "query", type: "string", isRequired: false, translationKey: "saleStatusFilter" },
      { name: "payment_type", location: "query", type: "string", isRequired: false, translationKey: "salePaymentTypeFilter" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleStartingAfter" },
      { name: "ending_before", location: "query", type: "string (uuid)", isRequired: false, translationKey: "saleEndingBefore" },
    ],
    example: {
      path: `/v1/concessions/${CONCESSION_UUID}/sales`,
      query: { event: EVENT_UUID },
      body: null,
    },
    response: SALES_LIST_EXAMPLE,
  },
  {
    id: "getPartnerEventTotals",
    method: "GET",
    path: "/v1/events/{uuid}/totals",
    group: "totals",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/totals`,
      query: {},
      body: null,
    },
    response: EVENT_TOTALS_EXAMPLE,
  },
  {
    id: "listPartnerEventConcessionTotals",
    method: "GET",
    path: "/v1/events/{uuid}/totals/concessions",
    group: "totals",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string", isRequired: false, translationKey: "aggregateStartingAfter" },
      { name: "ending_before", location: "query", type: "string", isRequired: false, translationKey: "aggregateEndingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/totals/concessions`,
      query: {},
      body: null,
    },
    response: CONCESSION_TOTALS_EXAMPLE,
  },
  {
    id: "listPartnerEventPaymentTypeTotals",
    method: "GET",
    path: "/v1/events/{uuid}/totals/payment-types",
    group: "totals",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string", isRequired: false, translationKey: "paymentTypeStartingAfter" },
      { name: "ending_before", location: "query", type: "string", isRequired: false, translationKey: "paymentTypeEndingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/totals/payment-types`,
      query: {},
      body: null,
    },
    response: PAYMENT_TYPE_TOTALS_EXAMPLE,
  },
  {
    id: "listPartnerEventProductTotals",
    method: "GET",
    path: "/v1/events/{uuid}/totals/products",
    group: "totals",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string", isRequired: false, translationKey: "aggregateStartingAfter" },
      { name: "ending_before", location: "query", type: "string", isRequired: false, translationKey: "aggregateEndingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/totals/products`,
      query: { limit: "100" },
      body: null,
    },
    response: PRODUCT_TOTALS_EXAMPLE,
  },
  {
    id: "listPartnerEventCategoryTotals",
    method: "GET",
    path: "/v1/events/{uuid}/totals/categories",
    group: "totals",
    scope: "sales:read",
    params: [
      AUTHORIZATION_HEADER,
      { name: "uuid", location: "path", type: "string (uuid)", isRequired: true, translationKey: "eventPath" },
      { name: "limit", location: "query", type: "integer", isRequired: false, translationKey: "limit" },
      { name: "starting_after", location: "query", type: "string", isRequired: false, translationKey: "aggregateStartingAfter" },
      { name: "ending_before", location: "query", type: "string", isRequired: false, translationKey: "aggregateEndingBefore" },
    ],
    example: {
      path: `/v1/events/${EVENT_UUID}/totals/categories`,
      query: {},
      body: null,
    },
    response: CATEGORY_TOTALS_EXAMPLE,
  },
];

/**
 * The stable error codes of the contract. `code` is English and never changes with the request
 * language, which is why an integrator branches on it and never on the message.
 */
export const DOCS_ERRORS: DocsError[] = [
  { code: "invalid_request", httpCode: 400, translationKey: "invalidRequest" },
  { code: "invalid_date_range", httpCode: 400, translationKey: "invalidDateRange" },
  { code: "invalid_sale_status", httpCode: 400, translationKey: "invalidSaleStatus" },
  { code: "invalid_payment_type", httpCode: 400, translationKey: "invalidPaymentType" },
  { code: "invalid_client", httpCode: 401, translationKey: "invalidClient" },
  { code: "credential_revoked", httpCode: 401, translationKey: "credentialRevoked" },
  { code: "credential_expired", httpCode: 401, translationKey: "credentialExpired" },
  { code: "invalid_token", httpCode: 401, translationKey: "invalidToken" },
  { code: "ip_not_allowed", httpCode: 403, translationKey: "ipNotAllowed" },
  { code: "insufficient_scope", httpCode: 403, translationKey: "insufficientScope" },
  { code: "venue_out_of_scope", httpCode: 403, translationKey: "venueOutOfScope" },
  { code: "not_found", httpCode: 404, translationKey: "notFound" },
  { code: "rate_limit_exceeded", httpCode: 429, translationKey: "rateLimitExceeded" },
  { code: "server_error", httpCode: 500, translationKey: "serverError" },
];

/** Error body shape, so the shape of what an integrator has to parse is documented once. */
export const ERROR_BODY_EXAMPLE: string = `{
  "http_code": 400,
  "error_code": "invalid_date_range",
  "message": "The date range cannot span more than 92 days.",
  "is_success": false,
  "type": "warning"
}`;

/**
 * The gateway answers this plain body, without `error_code`, when the token never reaches the
 * service. A client that only parses `error_code` breaks on it, so it is documented on its own.
 */
export const GATEWAY_UNAUTHORIZED_EXAMPLE: string = `{
  "message": "Unauthorized"
}`;

export const DOCS_ENDPOINT_GROUP_ORDER: DocsEndpoint["group"][] = [
  "authentication",
  "structure",
  "catalogs",
  "sales",
  "totals",
];

/** Groups the endpoints for the reference page, keeping the order the contract lists them in. */
export const getEndpointsByGroup = (
  group: DocsEndpoint["group"]
): DocsEndpoint[] => {
  return DOCS_ENDPOINTS.filter((endpoint) => endpoint.group === group);
};

/** Looks up one operation by its operationId, for the pages that document a single route. */
export const getEndpointById = (id: string): DocsEndpoint | undefined => {
  return DOCS_ENDPOINTS.find((endpoint) => endpoint.id === id);
};
